// @pages/account/CreateAccount.page.tsx
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { renderRoute, routeChooseSubscriptionWithStatus, routeHome, routeLogin } from "@navigation/typedRoutes";
import { useOrganizationSignupClient } from "@contexts/OrganizationSignupClient.context";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useJwtStateHandles } from "@contexts/Jwt.context";
import { useLoadingController } from "@contexts/Loading.context";
import CreateAccountView from "@view/authentication/CreateAccount";
import {
  buildCreateAccountViewProps,
} from "@ps-bridge/createAccount.program";
import type {
  CreateAccountKernel,
  CreateAccountViewProps,
} from "@ps-bridge/contracts/createAccount";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useIoState } from "@utils/useIoState";

import { ok, type Ok } from "@ps-bridge/ok";
import { makeEmail } from "@ps-bridge/models/brands/email";
import { makePassword } from "@ps-bridge/models/brands/password";
import * as PSAuthRules from "@ps-bridge/models/core/authRules";
import * as PSErrorHandlers from "@ps-bridge/models/core/errorHandlers";
import * as PSOption from "@utils/Option";
import { mkNavigate, mkTaskHandler, mkTaskRunner } from "@ps-bridge/capabilities";
import type { OptionTs, TaskTs } from "@ps-bridge/interop";
import type { ErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import type { HttpStatusCode } from "@ps-bridge/models/brands/httpStatusCode";
import type { Int } from "@ps-bridge/prim";
import * as IntMath from "@ps-bridge/data/int";
import type { EmitOnInit, StateRead, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { PlanTier } from "@ps-bridge/models/clients/organizationSignup";
import { setPendingEmailValidation } from "@ps-bridge/services/pendingEmailValidation";
import { unwrap as unwrapJwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import {
  ioEffect,
  taskChain,
  taskEitherChain,
  taskEitherFromEffect,
  taskEitherMap,
  taskEitherWithLoading,
  taskFromIO,
} from "@utils/effects/branded";
import { useOnInit } from "@hooks/effects/useIoEffect";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { enabledFeatures, FeaturesEnum } from "@src/utils/enabledFeatures";

// Updated due to issues with stripe to redirect to home page
type CreateAccountSquareBillingInput =
  CreateAccountViewProps extends { readonly squareBilling: OptionTs<infer Value> } ? Value : never;
type CreateAccountNavigation =
  CreateAccountKernel extends { readonly navigation: infer Value } ? Value : never;
type CreateAccountLoginRoute =
  CreateAccountNavigation extends { readonly loginRoute: infer Value } ? Value : never;

const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const organizationSignupClient = useOrganizationSignupClient();
  const authenticationClient = useAuthenticationClient();
  const { isTokenExpiring, subscriptionStatus } = useJwtStateHandles();
  const loadingController = useLoadingController();
  const runTaskWithError = useRunTaskWithError();
  const { setLoading, setSuccess, setError } = loadingController;
  const pendingEmailKey = "pendingEmailValidationEmail";
  const allowSquareBillingStubCapture = enabledFeatures[FeaturesEnum.StubClients];

  const modeState = useIoState<string>("choose");
  const stepState = useIoState<Int>(IntMath.round(0) as Int);
  const formErrorState = useIoOptionState<string>(PSOption.none());
  const emailState = useIoState<string>("");
  const firstNameState = useIoState<string>("");
  const lastNameState = useIoState<string>("");
  const passwordState = useIoState<string>("");
  const confirmPasswordState = useIoState<string>("");
  const organizationNameState = useIoState<string>("");
  const goalsState = useIoState<string[]>([]);
  const planTierState = useIoOptionState<PlanTier>(PSOption.none());
  const squareBillingState = useIoOptionState<CreateAccountSquareBillingInput>(PSOption.none());
  const billingNameState = useIoState<string>("");
  const billingCardState = useIoState<string>("");
  const billingExpState = useIoState<string>("");
  const billingCvcState = useIoState<string>("");
  const signupCompleteState = useIoState<boolean>(false);
  const { read: modeRead, set: modeSet } = modeState;
  const stepRead = stepState.read as StateRead<Int>;
  const stepSet = stepState.set as StateUpdate<Int>;
  const { read: formErrorRead, setSome: formErrorSetSome, setMaybe: formErrorSetMaybe } = formErrorState;
  const { read: emailRead, set: emailSet } = emailState;
  const { read: firstNameRead, set: firstNameSet } = firstNameState;
  const { read: lastNameRead, set: lastNameSet } = lastNameState;
  const { read: passwordRead, set: passwordSet } = passwordState;
  const { read: confirmPasswordRead, set: confirmPasswordSet } = confirmPasswordState;
  const { read: organizationNameRead, set: organizationNameSet } = organizationNameState;
  const { read: goalsRead, set: goalsSet } = goalsState;
  const { read: planTierRead, setSome: planTierSetSome, setMaybe: planTierSetMaybe } = planTierState;
  const { read: squareBillingRead, setSome: squareBillingSetSome, setMaybe: squareBillingSetMaybe } = squareBillingState;
  const { read: billingNameRead, set: billingNameSet } = billingNameState;
  const { read: billingCardRead, set: billingCardSet } = billingCardState;
  const { read: billingExpRead, set: billingExpSet } = billingExpState;
  const { read: billingCvcRead, set: billingCvcSet } = billingCvcState;
  const { read: signupCompleteRead, set: signupCompleteSet } = signupCompleteState;

  const navigateAction = useMemo(
    () =>
      mkNavigate((route) =>
        ioEffect(() => {
          navigate(renderRoute(route));
          return ok;
        }),
      ),
    [navigate],
  );

  const handleAuthenticationError = useCallback(
    ({
      type,
      message,
      statusCode,
    }: {
      type: string;
      message: ErrorMessageText;
      statusCode: OptionTs<HttpStatusCode>;
    }) => {
      const { message: formatted, info } = PSErrorHandlers.authenticationErrorMessage({
        type,
        message,
        statusCode,
      });
      return setError({ message: formatted, info });
    },
    [setError],
  );

  const proceedToAppTask = useCallback(
    (email: string, password: string): TaskTs<Ok> =>
      taskEitherWithLoading(
        setLoading,
        () =>
          taskEitherChain(
            taskEitherFromEffect(
              authenticationClient.login({
                email: makeEmail(email),
                password: makePassword(password),
              }),
              (reason) =>
                reason as {
                  type: string;
                  message: ErrorMessageText;
                  statusCode: OptionTs<HttpStatusCode>;
                },
            ),
            (_loginStatus) =>
              taskEitherMap(
                taskEitherFromEffect(
                  authenticationClient.refreshJwtToken,
                  (reason) =>
                    reason as {
                      type: string;
                      message: ErrorMessageText;
                      statusCode: OptionTs<HttpStatusCode>;
                    },
                ),
                (response) => response.subscription_status,
              ),
          ),
        (error) =>
          taskFromIO(
            handleAuthenticationError({
              type: error.type,
              message: error.message,
              statusCode: error.statusCode,
            }),
          ),
        (status) =>
          taskChain(
            taskFromIO(subscriptionStatus.set(status)),
            () =>
              taskChain(
                taskFromIO(isTokenExpiring.set(false)),
                () =>
                taskChain(
                taskFromIO(setSuccess),
                () =>
                  taskFromIO(
                    ioEffect(() => {
                      setPendingEmailValidation(true);
                      if (typeof window !== "undefined" && window.localStorage) {
                        window.localStorage.removeItem("jwtRefreshDisabled");
                        window.localStorage.setItem(pendingEmailKey, email);
                        window.setTimeout(() => {
                          const redirect = PSAuthRules.subscriptionRedirect(unwrapJwtSubscriptionStatus(status));
                          if (redirect === "home") {
                            navigate(renderRoute(routeHome));
                            return;
                          }
                          if (redirect === "chooseSubscription") {
                            navigate(renderRoute(routeChooseSubscriptionWithStatus(status)));
                          }
                        }, 0);
                      }
                      return ok;
                    }),
                  ),
                  ),
              ),
          ),
      ),
    [
      authenticationClient,
      handleAuthenticationError,
      isTokenExpiring,
      navigate,
      subscriptionStatus,
      pendingEmailKey,
      setLoading,
      setSuccess,
    ],
  );

  const onInitAction = useMemo(
    () =>
      ioEffect(() => {
        if (isTokenExpiring.read) {
          return ok;
        }
        const status = subscriptionStatus.read;
        const redirect = PSAuthRules.subscriptionRedirect(unwrapJwtSubscriptionStatus(status));
        if (redirect === "home") {
          navigate(renderRoute(routeHome));
          return ok;
        }
        if (redirect === "chooseSubscription") {
          navigate(renderRoute(routeChooseSubscriptionWithStatus(status)));
        }
        return ok;
      }),
    [isTokenExpiring.read, navigate, subscriptionStatus.read],
  );
  useOnInit(PSOption.some(onInitAction as EmitOnInit), [onInitAction]);

  const createAccountKernel = useMemo<CreateAccountKernel>(
    () => ({
      form: {
        mode: { read: modeRead, set: modeSet },
        step: { read: stepRead, set: stepSet },
        formError: { read: formErrorRead, setSome: formErrorSetSome, setMaybe: formErrorSetMaybe },
        email: { read: emailRead, set: emailSet },
        firstName: { read: firstNameRead, set: firstNameSet },
        lastName: { read: lastNameRead, set: lastNameSet },
        password: { read: passwordRead, set: passwordSet },
        confirmPassword: { read: confirmPasswordRead, set: confirmPasswordSet },
        organizationName: { read: organizationNameRead, set: organizationNameSet },
        goals: { read: goalsRead, set: goalsSet },
        planTier: { read: planTierRead, setSome: planTierSetSome, setMaybe: planTierSetMaybe },
        squareBilling: { read: squareBillingRead, setSome: squareBillingSetSome, setMaybe: squareBillingSetMaybe },
        billingName: { read: billingNameRead, set: billingNameSet },
        billingCard: { read: billingCardRead, set: billingCardSet },
        billingExp: { read: billingExpRead, set: billingExpSet },
        billingCvc: { read: billingCvcRead, set: billingCvcSet },
        signupComplete: { read: signupCompleteRead, set: signupCompleteSet },
      },
      signup: {
        client: organizationSignupClient,
      },
      proceedToApp: mkTaskHandler(({ email, password }) => proceedToAppTask(email, password)),
      taskRunner: mkTaskRunner((task) =>
        ioEffect(() => {
          runTaskWithError(task).then(() => ok);
          return ok;
        }),
      ),
      loading: loadingController,
      navigation: {
        navigate: navigateAction,
        loginRoute: routeLogin as CreateAccountLoginRoute,
      },
    }),
    [
      billingCardRead,
      billingCardSet,
      billingCvcRead,
      billingCvcSet,
      billingExpRead,
      billingExpSet,
      billingNameRead,
      billingNameSet,
      confirmPasswordRead,
      confirmPasswordSet,
      emailRead,
      emailSet,
      firstNameRead,
      firstNameSet,
      formErrorRead,
      formErrorSetMaybe,
      formErrorSetSome,
      goalsRead,
      goalsSet,
      lastNameRead,
      lastNameSet,
      loadingController,
      modeRead,
      modeSet,
      navigateAction,
      organizationNameRead,
      organizationNameSet,
      organizationSignupClient,
      passwordRead,
      passwordSet,
      planTierRead,
      planTierSetMaybe,
      planTierSetSome,
      squareBillingRead,
      squareBillingSetMaybe,
      squareBillingSetSome,
      proceedToAppTask,
      runTaskWithError,
      signupCompleteRead,
      signupCompleteSet,
      stepRead,
      stepSet,
    ],
  );

  const viewProps = useMemo(
    () =>
      buildCreateAccountViewProps({
        kernel: createAccountKernel,
      }),
    [createAccountKernel],
  );

  return (
    <CreateAccountView
      {...viewProps}
      allowSquareBillingStubCapture={allowSquareBillingStubCapture}
    />
  );
};

export default CreateAccountPage;
